export interface FlatCategory {
  id: number
  name: string
  pathCache: string
  depthCache: number
}

export interface CategoryTreeNode extends FlatCategory {
  children: CategoryTreeNode[]
  selectable?: boolean
}

export const PATH_SEPARATOR = ' > '

export function parentPath(pathCache: string): string | null {
  const separatorIndex = pathCache.lastIndexOf(PATH_SEPARATOR)
  if (separatorIndex < 0) return null
  return pathCache.slice(0, separatorIndex)
}

function categoryNameFromPath(pathCache: string): string {
  const separatorIndex = pathCache.lastIndexOf(PATH_SEPARATOR)
  if (separatorIndex < 0) return pathCache
  return pathCache.slice(separatorIndex + PATH_SEPARATOR.length)
}

function depthFromPath(pathCache: string): number {
  if (!pathCache.includes(PATH_SEPARATOR)) return 0
  return pathCache.split(PATH_SEPARATOR).length - 1
}

export function syntheticCategoryId(pathCache: string): number {
  let hash = 0
  for (let i = 0; i < pathCache.length; i += 1) {
    hash = (hash * 31 + pathCache.charCodeAt(i)) % 2147483647
  }
  return hash === 0 ? -1 : -hash
}

export function buildCategoryTree(flat: FlatCategory[]): CategoryTreeNode[] {
  const byPath = new Map<string, CategoryTreeNode>()

  const getOrCreate = (pathCache: string, selectable: boolean, source?: FlatCategory): CategoryTreeNode => {
    const existing = byPath.get(pathCache)
    if (existing) {
      if (source) {
        existing.id = source.id
        existing.name = source.name
        existing.depthCache = source.depthCache
        existing.selectable = true
      }
      return existing
    }

    const node: CategoryTreeNode = {
      id: source?.id ?? syntheticCategoryId(pathCache),
      name: source?.name ?? categoryNameFromPath(pathCache),
      pathCache,
      depthCache: source?.depthCache ?? depthFromPath(pathCache),
      selectable: source ? true : selectable,
      children: []
    }
    byPath.set(pathCache, node)
    return node
  }

  for (const cat of flat) {
    getOrCreate(cat.pathCache, true, cat)

    let ancestorPath = parentPath(cat.pathCache)
    while (ancestorPath) {
      getOrCreate(ancestorPath, false)
      ancestorPath = parentPath(ancestorPath)
    }
  }

  const roots: CategoryTreeNode[] = []
  for (const node of byPath.values()) {
    const parentPathCache = parentPath(node.pathCache)
    if (parentPathCache) {
      const parent = byPath.get(parentPathCache)
      if (parent && !parent.children.some(child => child.pathCache === node.pathCache)) {
        parent.children.push(node)
      }
    } else {
      roots.push(node)
    }
  }

  const sortNodes = (nodes: CategoryTreeNode[]) => {
    nodes.sort((a, b) => a.pathCache.localeCompare(b.pathCache))
    nodes.forEach(child => sortNodes(child.children))
  }
  sortNodes(roots)

  return roots
}

export function indexCategoriesById(flat: FlatCategory[]): Map<number, FlatCategory> {
  return new Map(flat.map(category => [category.id, category]))
}

export function parentPathDisplay(pathCache: string): string | null {
  const parent = parentPath(pathCache)
  if (!parent) return null
  return parent.split(PATH_SEPARATOR).join(' › ')
}

export function findNodeByPath(nodes: CategoryTreeNode[], pathCache: string): CategoryTreeNode | undefined {
  for (const node of nodes) {
    if (node.pathCache === pathCache) return node
    const found = findNodeByPath(node.children, pathCache)
    if (found) return found
  }
  return undefined
}

export function ancestorPathsForCategory(pathCache: string): string[] {
  const paths: string[] = []
  let currentPath = parentPath(pathCache)
  while (currentPath) {
    paths.unshift(currentPath)
    currentPath = parentPath(currentPath)
  }
  return paths
}

export function descendantPaths(tree: CategoryTreeNode[], pathCache: string): string[] {
  const node = findNodeByPath(tree, pathCache)
  if (!node) return []

  const paths: string[] = []
  const collect = (current: CategoryTreeNode) => {
    current.children.forEach(child => {
      paths.push(child.pathCache)
      collect(child)
    })
  }
  collect(node)
  return paths
}

export function siblingPaths(tree: CategoryTreeNode[], pathCache: string): string[] {
  const parentPathCache = parentPath(pathCache)
  const siblings = parentPathCache ? (findNodeByPath(tree, parentPathCache)?.children ?? []) : tree
  return siblings.filter(node => node.pathCache !== pathCache).map(node => node.pathCache)
}

export function toggleExpandedPaths(
  expandedPaths: Set<string>,
  tree: CategoryTreeNode[],
  pathCache: string
): Set<string> {
  const next = new Set(expandedPaths)

  if (next.has(pathCache)) {
    next.delete(pathCache)
    descendantPaths(tree, pathCache).forEach(path => next.delete(path))
    return next
  }

  siblingPaths(tree, pathCache).forEach(siblingPath => {
    next.delete(siblingPath)
    descendantPaths(tree, siblingPath).forEach(path => next.delete(path))
  })
  next.add(pathCache)
  return next
}

export function filterCategoriesByQuery(categories: FlatCategory[], query: string): FlatCategory[] {
  const normalizedQuery = query.trim().toLocaleLowerCase()
  if (!normalizedQuery) return []

  return categories.filter(
    category =>
      category.name.toLocaleLowerCase().includes(normalizedQuery) ||
      category.pathCache.toLocaleLowerCase().includes(normalizedQuery)
  )
}
